/**
 * @file xcf-utils.ts
 * @description Utilities and helper functions for GIMP XCF format decoding.
 */

/**
 * Decodes RLE-compressed tiled planar data from XCF.
 */
export function decodeTiledPlanar(data: Uint8Array, bpp: number, tw: number, th: number): Uint8Array[] {
  const channels: Uint8Array[] = [];
  let inPos = 0;
  const expectedChannelSize = tw * th;

  for (let c = 0; c < bpp; c++) {
    const out = new Uint8Array(expectedChannelSize);
    let outPos = 0;
    while (outPos < expectedChannelSize && inPos < data.length) {
      const b = data[inPos++];
      if (b < 127) {
        // Repeat Run
        const count = b + 1;
        const val = data[inPos++];
        const toFill = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toFill; i++) {
          out[outPos++] = val;
        }
      } else if (b === 127) {
        // Long Repeat
        const count = (data[inPos] << 8 | data[inPos + 1]) + 1;
        inPos += 2;
        const val = data[inPos++];
        const toFill = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toFill; i++) {
          out[outPos++] = val;
        }
      } else if (b === 128) {
        // Long Literal
        const count = (data[inPos] << 8 | data[inPos + 1]) + 1;
        inPos += 2;
        const toCopy = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toCopy; i++) {
          out[outPos++] = data[inPos++];
        }
        if (toCopy < count) inPos += (count - toCopy);
      } else {
        // Short Literal
        const count = 256 - b;
        const toCopy = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toCopy; i++) {
          out[outPos++] = data[inPos++];
        }
        if (toCopy < count) inPos += (count - toCopy);
      }
    }
    channels.push(out);
  }
  return channels;
}

/**
 * Maps XCF internal mode integers to standard composite mode strings.
 */
export function mapXcfBlendMode(mode: number): string {
  const modes: Record<number, string> = {
    0: "normal",
    1: "dissolve",
    2: "behind",
    3: "multiply",
    4: "screen",
    5: "overlay",
    6: "difference",
    7: "addition",
    8: "subtract",
    9: "darken-only",
    10: "lighten-only",
    11: "hue",
    12: "saturation",
    13: "color",
    14: "value"
  };
  return modes[mode] || "normal";
}
